import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { RequireRole } from './auth/RequireRole';
import { AppShell } from './components/layout/AppShell';
import { ChangePasswordPage } from './modules/auth/ChangePasswordPage';
import { DashboardPage } from './modules/dashboard/DashboardPage';
import { EstimatesListPage } from './modules/estimates/EstimatesListPage';
import { EstimatorPage } from './modules/estimates/EstimatorPage';
import { LoginPage } from './modules/auth/LoginPage';
import { NotFoundPage } from './modules/not-found/NotFoundPage';
import { SavedEstimatesPage } from './modules/simulator/SavedEstimatesPage';
import { SimulatorPage } from './modules/simulator/SimulatorPage';
import { SimulatorShell } from './modules/simulator/SimulatorShell';
import { EquationsPage } from './modules/settings/equations/EquationsPage';
import { UsersPage } from './modules/settings/users/UsersPage';
import { TemplateDetailPage } from './modules/templates/detail/TemplateDetailPage';
import { TemplatesListPage } from './modules/templates/TemplatesListPage';
import { TemplateValuesForm } from './modules/templates/TemplateValuesForm';
import { VehicleDetailPage } from './modules/vehicles/detail/VehicleDetailPage';
import { VehiclesListPage } from './modules/vehicles/VehiclesListPage';

/**
 * Every route of plan §7 exists from phase 0, so the nav, the deep links and the role gates are honest
 * today and each later phase replaces one placeholder with the real module:
 * the documents tab in phase 5. The equation editor arrived in phase 1, the templates module in phase 2,
 * the dashboard, the estimator and the estimates list in phase 3, and the offer step, the vehicles list
 * and the stock-vehicle dossier in phase 4.
 */
export const AppRouter = () => (
  <Routes>
    <Route
      path="/login"
      element={<LoginPage />}
    />

    <Route element={<ProtectedRoute />}>
      {/* Outside the shell on purpose: a forced password change offers no navigation. */}
      <Route
        path="/change-password"
        element={<ChangePasswordPage />}
      />

      <Route element={<AppShell />}>
        <Route
          index
          element={
            <Navigate
              to="/dashboard"
              replace
            />
          }
        />
        <Route
          path="/dashboard"
          element={<DashboardPage />}
        />

        {/* Declared before `/estimates/:id` so the estimator's static path wins over the param route. */}
        <Route
          path="/estimates/new"
          element={<EstimatorPage />}
        />
        <Route
          path="/estimates/:id"
          element={<EstimatorPage />}
        />
        <Route
          path="/estimates"
          element={<EstimatesListPage />}
        />

        <Route
          path="/vehicles/:id"
          element={<VehicleDetailPage />}
        />
        <Route
          path="/vehicles"
          element={<VehiclesListPage />}
        />

        {/* The draft editor's static tail is declared before the `:id` detail route (plan §2.4). */}
        <Route
          path="/templates/:id/draft"
          element={<TemplateValuesForm />}
        />
        <Route
          path="/templates/:id"
          element={<TemplateDetailPage />}
        />
        <Route
          path="/templates"
          element={<TemplatesListPage />}
        />

        <Route
          path="/settings/equations"
          element={
            <RequireRole roles={['ADMIN']}>
              <EquationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/settings/users"
          element={
            <RequireRole roles={['ADMIN']}>
              <UsersPage />
            </RequireRole>
          }
        />
      </Route>
    </Route>

    {/* The depreciation simulator: a public testing tool, outside the session guard and the app shell on purpose. */}
    <Route
      path="/simulator"
      element={<SimulatorShell />}
    >
      <Route
        index
        element={<SimulatorPage />}
      />
      <Route
        path="estimates"
        element={<SavedEstimatesPage />}
      />
    </Route>

    <Route
      path="*"
      element={<NotFoundPage />}
    />
  </Routes>
);
